/**
 * Outlet setup (org) contracts (03-modules.md §1, 04-data-model.md "org",
 * 05-api.md "Outlet setup"): company, outlet profile and settings, sections,
 * tables (+ signed QR), timing groups, other charges, payment methods, order
 * sources, terminals, feature switches and the till's bootstrap bundle.
 *
 * Master data is created and changed with "upsert" commands keyed by a client-
 * generated UUIDv7 (safe to create offline, CLAUDE.md) and archived, never deleted
 * (`archivedAt`). Every mutable row carries `version` (05-api.md "Concurrency").
 * Charges here are master data only: the calculator is `packages/pricing`.
 */
import { z } from 'zod';
import { isoTimestampSchema } from './common.js';
import { defineEvent } from './events.js';
import { deviceSecretSchema } from './identity.js';
import { uuidSchema, uuidV7Schema } from './ids.js';
import { printerSchema, stationSchema } from './kitchen.js';
import { nonNegativePaiseSchema, percentBasisPointsSchema } from './money.js';
import { permissionKeySchema } from './permissions.js';
import { discountSchema, roleDiscountLimitSchema, taxGroupSchema } from './pricing.js';

// ---------------------------------------------------------------------------
// Shared primitives

/** A display name for master data (section, table, charge, …). */
export const masterNameSchema = z.string().trim().min(1).max(60);

/** Sort position within a list (lower first). */
export const sortSchema = z.int().min(0).max(10_000);

/** Wall-clock time of day, `HH:MM` (24 h), in the outlet's timezone. */
export const timeOfDaySchema = z
  .string()
  .regex(/^([01]\d|2[0-3]):[0-5]\d$/, { message: 'Expected a time as HH:MM (24 hour)' });

/** A pricing context a section uses (03-modules.md §1 Rules; `pricing` reads it). */
export const SECTION_PRICING_CONTEXTS = ['dine_in', 'takeaway', 'delivery'] as const;
export type SectionPricingContext = (typeof SECTION_PRICING_CONTEXTS)[number];

/**
 * Every pricing context (03-modules.md §3 price model; `orders.channel`): the
 * section contexts plus the channels that price differently.
 */
export const PRICE_CONTEXTS = [
  'dine_in',
  'takeaway',
  'delivery',
  'qr',
  'swiggy',
  'zomato',
] as const;
export type PriceContext = (typeof PRICE_CONTEXTS)[number];

/** `If-Match: "v3"` → 3 (05-api.md "Concurrency"). Undefined when absent or not a version tag. */
export function parseIfMatch(header: unknown): number | undefined {
  if (typeof header !== 'string') return undefined;
  const m = /^\s*(?:W\/)?"v(\d{1,9})"\s*$/.exec(header);
  return m ? Number(m[1]) : undefined;
}

/** `ETag` value for a mutable aggregate's version: `"v{version}"`. */
export function versionEtag(version: number): string {
  return `"v${version}"`;
}

// ---------------------------------------------------------------------------
// Identifiers printed on bills (GSTIN, FSSAI) and the outlet address

const GSTIN_CHARSET = '0123456789ABCDEFGHIJKLMNOPQRSTUVWXYZ';
const GSTIN_PATTERN = /^[0-9]{2}[A-Z]{5}[0-9]{4}[A-Z][1-9A-Z]Z[0-9A-Z]$/;

/** The GSTIN check character (mod-36 Luhn variant) for its first 14 characters. */
export function gstinCheckChar(first14: string): string {
  let sum = 0;
  for (let i = 0; i < 14; i += 1) {
    const value = GSTIN_CHARSET.indexOf(first14.charAt(i));
    if (value < 0) throw new RangeError('Not a GSTIN character');
    const product = value * (i % 2 === 0 ? 1 : 2);
    sum += Math.floor(product / 36) + (product % 36);
  }
  return GSTIN_CHARSET.charAt((36 - (sum % 36)) % 36);
}

/** Format (state code, PAN, entity, `Z`) and check character. */
export function isValidGstin(value: string): boolean {
  return GSTIN_PATTERN.test(value) && gstinCheckChar(value.slice(0, 14)) === value.charAt(14);
}

/** 15-character GSTIN, uppercased, with a valid check character. */
export const gstinSchema = z
  .string()
  .trim()
  .toUpperCase()
  .regex(GSTIN_PATTERN, { message: 'Expected a 15-character GSTIN', abort: true })
  .refine(isValidGstin, { message: 'The GSTIN check character does not match' });

/** FSSAI licence or registration number: 14 digits. */
export const fssaiNoSchema = z
  .string()
  .trim()
  .regex(/^\d{14}$/, { message: 'Expected a 14-digit FSSAI number' });

/** The outlet's postal address, as printed on bills. */
export const outletAddressSchema = z.object({
  line1: z.string().trim().min(1).max(120),
  line2: z.string().trim().max(120).optional(),
  city: z.string().trim().min(1).max(60),
  state: z.string().trim().min(1).max(60),
  pincode: z
    .string()
    .trim()
    .regex(/^[1-9]\d{5}$/, { message: 'Expected a 6-digit PIN code' }),
});
export type OutletAddress = z.output<typeof outletAddressSchema>;

/** WGS 84 position of the outlet (delivery radius, "nearby" search). Not money. */
export const geoPointSchema = z.object({
  lat: z.number().min(-90).max(90),
  lng: z.number().min(-180).max(180),
});

function isTimeZone(value: string): boolean {
  try {
    new Intl.DateTimeFormat('en', { timeZone: value });
    return true;
  } catch {
    return false;
  }
}

/** An IANA timezone (`Asia/Kolkata`). */
export const timezoneSchema = z
  .string()
  .min(1)
  .max(64)
  .refine(isTimeZone, { message: 'Expected an IANA timezone such as Asia/Kolkata' });

/** Short outlet code (bills, exports, the storefront subdomain): letters, digits, dashes. */
export const outletCodeSchema = z
  .string()
  .trim()
  .toUpperCase()
  .regex(/^[A-Z0-9][A-Z0-9-]{0,29}$/, {
    message: 'Expected 1-30 letters, digits or dashes, starting with a letter or digit',
  });

export const COMPANY_STATUSES = ['active', 'suspended', 'closed'] as const;
export const OUTLET_STATUSES = ['active', 'suspended', 'closed'] as const;

/** v3.0 trades in rupees only (03-modules.md §1). */
export const OUTLET_CURRENCY = 'INR';

// ---------------------------------------------------------------------------
// Company and outlet

export const companySchema = z.object({
  id: uuidSchema,
  name: z.string(),
  legalName: z.string().nullable(),
  planRef: z.string().nullable(),
  status: z.enum(COMPANY_STATUSES),
  createdAt: isoTimestampSchema,
  version: z.int(),
});

/** `POST /v1/admin/companies` (QRkey staff onboard a restaurant). */
export const createCompanySchema = z.object({
  /**
   * The new company's id, minted by the caller (UUIDv7). The company is its own
   * tenant: the request's Idempotency-Key is kept there, so a retry must name it.
   */
  id: uuidV7Schema,
  name: masterNameSchema,
  legalName: z.string().trim().min(1).max(120).optional(),
  planRef: z.string().trim().min(1).max(60).optional(),
});

export const outletSchema = z.object({
  id: uuidSchema,
  companyId: uuidSchema,
  name: z.string(),
  code: z.string(),
  gstin: z.string().nullable(),
  fssaiNo: z.string().nullable(),
  address: outletAddressSchema.nullable(),
  geo: geoPointSchema.nullable(),
  timezone: z.string(),
  dayCloseTime: timeOfDaySchema,
  currency: z.literal(OUTLET_CURRENCY),
  status: z.enum(OUTLET_STATUSES),
  legacyEntityId: z.int().nullable(),
  createdAt: isoTimestampSchema,
  updatedAt: isoTimestampSchema,
  version: z.int(),
});
export type OutletView = z.output<typeof outletSchema>;

const outletProfileFields = {
  name: masterNameSchema,
  code: outletCodeSchema,
  gstin: gstinSchema.nullable(),
  fssaiNo: fssaiNoSchema.nullable(),
  address: outletAddressSchema.nullable(),
  geo: geoPointSchema.nullable(),
  timezone: timezoneSchema,
  /** When the business day ends (day close; `business_date` boundary). */
  dayCloseTime: timeOfDaySchema,
  currency: z.literal(OUTLET_CURRENCY),
};

/** `POST /v1/outlets` (F1 step 1, `org.createOutlet`). Timezone defaults to Asia/Kolkata, day close 04:00. */
export const createOutletSchema = z.object({
  id: uuidSchema.optional(),
  name: outletProfileFields.name,
  code: outletProfileFields.code,
  gstin: gstinSchema.optional(),
  fssaiNo: fssaiNoSchema.optional(),
  address: outletAddressSchema.optional(),
  geo: geoPointSchema.optional(),
  timezone: timezoneSchema.optional(),
  dayCloseTime: timeOfDaySchema.optional(),
  currency: outletProfileFields.currency.optional(),
});

/** `PATCH {o}` (`org.updateOutletProfile`): any subset; `null` clears an optional field. */
export const updateOutletProfileSchema = z
  .object(outletProfileFields)
  .partial()
  .refine((v) => Object.keys(v).length > 0, { message: 'Send at least one field to change' });

export const outletListSchema = z.object({ data: z.array(outletSchema) });

export const outletParamsSchema = z.object({ outletId: uuidSchema });

// ---------------------------------------------------------------------------
// Outlet settings (04-data-model.md `outlet_setting`: key → jsonb value)

/** Round-off of the bill total; the same modes `packages/pricing` implements. */
export const ROUND_OFF_MODES = ['none', 'nearest_rupee', 'up_rupee', 'down_rupee'] as const;

/**
 * The registered UI themes (docs/design/theming.md; packages/ui `THEMES`, kept equal
 * by a test in apps/staff). Adding a theme adds its id here too.
 */
export const THEME_IDS = ['calm-counter', 'night-service', 'colour-tiles'] as const;
export type ThemeId = (typeof THEME_IDS)[number];

/**
 * The settings an outlet can change, with their value schemas and defaults.
 * A missing row means the default. TODO(legacy): map the matching `entity_details`
 * columns (EntityMaster) in the org backfill.
 */
export const OUTLET_SETTINGS = {
  /** KOT per round with only the new lines, or the full order each time. */
  'kot.mode': { schema: z.enum(['incremental', 'full']), default: 'incremental' },
  /** Print the bill on the billing terminal's bill printer as soon as an order is billed (F2 step 8). */
  'bill.auto_print': { schema: z.boolean(), default: true },
  /** 11-open-questions.md Q3: deduct stock on KOT (default) or on settlement. */
  'inventory.deduct_on': { schema: z.enum(['kot', 'settle']), default: 'kot' },
  /**
   * Inventory (M5 task 2): `true` (default, outlets keep selling) lets a balance go below zero
   * and reports it; `false` refuses a wastage, move or count that would take it below zero and
   * makes order deduction stop at zero.
   */
  'inventory.allow_negative': { schema: z.boolean(), default: true },
  /**
   * Wastage worth more than this (paise) waits for someone with `inventory.wastage.approve`.
   * 0 = no limit (every wastage is approved as recorded).
   */
  'inventory.wastage_approval_above_paise': {
    schema: z.int().min(0).max(100_000_000_00),
    default: 0,
  },
  /**
   * Bar (M5 task 3), when the `bar` feature switch is on: how a liquor order gets its permit
   * number. `auto` assigns the next available one when the order is settled; `manual` leaves
   * it to someone with `bar.permits.assign` (`POST {o}/orders/{id}/permit`).
   */
  'bar.permit_assign': { schema: z.enum(['auto', 'manual']), default: 'auto' },
  /**
   * Bar: day close waits for a closing count of every liquor item (06-flows.md F9 step 4).
   * Overridable with `day.close_force` like open orders; turn it off for an outlet that counts
   * its bar some other way.
   */
  'bar.closing_required': { schema: z.boolean(), default: true },
  round_off: { schema: z.enum(ROUND_OFF_MODES), default: 'nearest_rupee' },
  /**
   * How the business day ends (accounting, M3 task 5): `clock` follows the day-close
   * time; `manual` keeps a day open until the manager closes it (day close, M5).
   * Kept in step with `DAY_CLOSE_MODES` in accounting.ts (a test checks).
   */
  'day.close_mode': { schema: z.enum(['clock', 'manual']), default: 'clock' },
  /**
   * Accounting part 2 (M5 task 8): an expense above this amount (paise) waits for
   * someone with `expenses.approve`; the cash has left the drawer, so it is recorded at
   * once and day close waits for the decision. 0 = no limit (every expense is approved
   * as recorded).
   */
  'expenses.approval_above_paise': { schema: z.int().min(0).max(100_000_000_00), default: 0 },
  /** QR orders land as pending_acceptance on the POS (03-modules.md §8). */
  'order.require_acceptance.qr': { schema: z.boolean(), default: true },
  /**
   * Diners may order from the table QR (storefront). Off: the QR shows the menu only
   * and orders are refused with QR_ORDERING_OFF.
   */
  'qr.ordering': { schema: z.boolean(), default: true },
  /**
   * Offer "pay now" (online, through the outlet's payment gateway) on QR orders.
   * "Pay at the counter" (request the bill) is always offered (03-modules.md §8).
   */
  'qr.pay_online': { schema: z.boolean(), default: true },
  /** Takeaway/delivery orders from the customer app need acceptance. */
  'order.require_acceptance.online': { schema: z.boolean(), default: true },
  /** 11-open-questions.md Q12: cash on delivery for own delivery, default off. */
  'delivery.cod': { schema: z.boolean(), default: false },
  /** The staff app's theme at this outlet until a person picks their own (theming.md). */
  'ui.theme': { schema: z.enum(THEME_IDS), default: 'calm-counter' },
  /** The customer app / QR menu theme of this outlet (theming.md). */
  'ui.customer_theme': { schema: z.enum(THEME_IDS), default: 'calm-counter' },
} as const;

export type OutletSettingKey = keyof typeof OUTLET_SETTINGS;
export const OUTLET_SETTING_KEYS = Object.keys(OUTLET_SETTINGS) as OutletSettingKey[];

type SettingsShape = { [K in OutletSettingKey]: (typeof OUTLET_SETTINGS)[K]['schema'] };
const settingsShape = Object.fromEntries(
  OUTLET_SETTING_KEYS.map((k) => [k, OUTLET_SETTINGS[k].schema]),
) as SettingsShape;

/** Every setting with its effective value (stored, else the default). */
export const outletSettingsSchema = z.object(settingsShape);
export type OutletSettings = z.output<typeof outletSettingsSchema>;

/** `PATCH {o}/settings`: any subset of the settings. */
export const updateOutletSettingsSchema = z
  .object(settingsShape)
  .partial()
  .strict()
  .refine((v) => Object.keys(v).length > 0, { message: 'Send at least one setting to change' });

export const DEFAULT_OUTLET_SETTINGS = Object.freeze(
  Object.fromEntries(OUTLET_SETTING_KEYS.map((k) => [k, OUTLET_SETTINGS[k].default])),
) as OutletSettings;

// ---------------------------------------------------------------------------
// Sections and tables

const archivable = {
  archivedAt: isoTimestampSchema.nullable(),
  createdAt: isoTimestampSchema,
  updatedAt: isoTimestampSchema,
  version: z.int(),
};

/** `?includeArchived=true` on master-data lists. */
export const masterListQuerySchema = z.object({
  includeArchived: z.enum(['true', 'false']).optional(),
});

export const sectionSchema = z.object({
  id: uuidSchema,
  outletId: uuidSchema,
  name: z.string(),
  pricingContext: z.enum(SECTION_PRICING_CONTEXTS),
  sort: z.int(),
  ...archivable,
});
export type SectionView = z.output<typeof sectionSchema>;

const sectionFields = {
  name: masterNameSchema,
  pricingContext: z.enum(SECTION_PRICING_CONTEXTS),
  sort: sortSchema,
};
export const createSectionSchema = z.object({
  id: uuidSchema.optional(),
  name: sectionFields.name,
  pricingContext: sectionFields.pricingContext,
  sort: sortSchema.optional(),
});
export const updateSectionSchema = z
  .object(sectionFields)
  .partial()
  .refine((v) => Object.keys(v).length > 0, { message: 'Send at least one field to change' });

export const TABLE_STATUSES = ['active', 'out_of_service'] as const;

export const diningTableSchema = z.object({
  id: uuidSchema,
  outletId: uuidSchema,
  sectionId: uuidSchema,
  name: z.string(),
  seats: z.int(),
  status: z.enum(TABLE_STATUSES),
  sort: z.int(),
  /** Bumped by `regenerate-qr`; printed QRs of older versions stop working. */
  qrVersion: z.int(),
  ...archivable,
});
export type DiningTableView = z.output<typeof diningTableSchema>;

const tableFields = {
  sectionId: uuidSchema,
  name: z.string().trim().min(1).max(30),
  seats: z.int().min(1).max(50),
  status: z.enum(TABLE_STATUSES),
  sort: sortSchema,
};
export const createTableSchema = z.object({
  id: uuidSchema.optional(),
  sectionId: tableFields.sectionId,
  name: tableFields.name,
  seats: tableFields.seats,
  status: tableFields.status.optional(),
  sort: sortSchema.optional(),
});
export const updateTableSchema = z
  .object(tableFields)
  .partial()
  .refine((v) => Object.keys(v).length > 0, { message: 'Send at least one field to change' });

export const sectionParamsSchema = z.object({ outletId: uuidSchema, sectionId: uuidSchema });
export const tableParamsSchema = z.object({ outletId: uuidSchema, tableId: uuidSchema });

/** `GET {o}/tables/qr-sheet.pdf?sectionId=`: one section's tables, or all. */
export const qrSheetQuerySchema = z.object({ sectionId: uuidSchema.optional() });

/**
 * A signed table QR token (02-architecture.md "Security baseline"):
 * `t1.<kid>.<payload>.<mac>` (base64url parts). Verified only by the server.
 */
export const tableTokenSchema = z
  .string()
  .max(200)
  .regex(/^t1\.[a-z0-9]{1,8}\.[A-Za-z0-9_-]{70}\.[A-Za-z0-9_-]{22}$/, {
    message: 'Not a table QR token',
  });

// ---------------------------------------------------------------------------
// Timing groups (menu availability and prices by time of day)

/** ISO weekday: 1 = Monday … 7 = Sunday. */
export const isoWeekdaySchema = z.int().min(1).max(7);

export const timingGroupSchema = z.object({
  id: uuidSchema,
  outletId: uuidSchema,
  name: z.string(),
  days: z.array(isoWeekdaySchema),
  /** `endTime` before `startTime` means the window runs past midnight. */
  startTime: timeOfDaySchema,
  endTime: timeOfDaySchema,
  sort: z.int(),
  ...archivable,
});
export type TimingGroupView = z.output<typeof timingGroupSchema>;

const daysSchema = z
  .array(isoWeekdaySchema)
  .min(1)
  .max(7)
  .refine((d) => new Set(d).size === d.length, { message: 'Each day at most once' });

const timingFields = {
  name: masterNameSchema,
  days: daysSchema,
  startTime: timeOfDaySchema,
  endTime: timeOfDaySchema,
  sort: sortSchema,
};
const distinctTimes = (v: { startTime?: string | undefined; endTime?: string | undefined }) =>
  v.startTime === undefined || v.endTime === undefined || v.startTime !== v.endTime;
const distinctTimesMessage = {
  message: 'Start and end times must differ',
  path: ['endTime'],
};

export const createTimingGroupSchema = z
  .object({
    id: uuidSchema.optional(),
    name: timingFields.name,
    days: timingFields.days,
    startTime: timingFields.startTime,
    endTime: timingFields.endTime,
    sort: sortSchema.optional(),
  })
  .refine(distinctTimes, distinctTimesMessage);
export const updateTimingGroupSchema = z
  .object(timingFields)
  .partial()
  .refine((v) => Object.keys(v).length > 0, { message: 'Send at least one field to change' })
  .refine(distinctTimes, distinctTimesMessage);

export const timingGroupParamsSchema = z.object({
  outletId: uuidSchema,
  timingGroupId: uuidSchema,
});

// ---------------------------------------------------------------------------
// Other charges (packaging, service, delivery, …): master data for `pricing`

export const CHARGE_KINDS = ['packaging', 'service', 'delivery', 'other'] as const;
export const CHARGE_CALCS = ['flat', 'percent'] as const;

export const otherChargeSchema = z.object({
  id: uuidSchema,
  outletId: uuidSchema,
  name: z.string(),
  kind: z.enum(CHARGE_KINDS),
  calc: z.enum(CHARGE_CALCS),
  /** `flat`: the amount in paise; null for `percent`. */
  amountPaise: nonNegativePaiseSchema.nullable(),
  /** `percent`: the rate in basis points (5% = 500); null for `flat`. */
  rateBp: z.int().nullable(),
  /** Pricing contexts the charge applies to. */
  appliesTo: z.array(z.enum(PRICE_CONTEXTS)),
  taxable: z.boolean(),
  /** A live tax group of this outlet (pricing); required when `taxable`, null otherwise. */
  taxGroupId: uuidSchema.nullable(),
  sort: z.int(),
  ...archivable,
});
export type OtherChargeView = z.output<typeof otherChargeSchema>;

const appliesToSchema = z
  .array(z.enum(PRICE_CONTEXTS))
  .min(1)
  .refine((c) => new Set(c).size === c.length, { message: 'Each context at most once' });

const chargeFields = {
  name: masterNameSchema,
  kind: z.enum(CHARGE_KINDS),
  calc: z.enum(CHARGE_CALCS),
  amountPaise: nonNegativePaiseSchema.nullable(),
  rateBp: percentBasisPointsSchema.nullable(),
  appliesTo: appliesToSchema,
  taxable: z.boolean(),
  taxGroupId: uuidSchema.nullable(),
  sort: sortSchema,
};

export const createOtherChargeSchema = z.object({
  id: uuidSchema.optional(),
  name: chargeFields.name,
  kind: chargeFields.kind,
  calc: chargeFields.calc,
  amountPaise: nonNegativePaiseSchema.optional(),
  rateBp: percentBasisPointsSchema.optional(),
  appliesTo: chargeFields.appliesTo,
  taxable: z.boolean().optional(),
  taxGroupId: uuidSchema.optional(),
  sort: sortSchema.optional(),
});
/** `amountPaise` goes with `calc: flat`, `rateBp` with `percent` (checked on the merged charge). */
export const updateOtherChargeSchema = z
  .object(chargeFields)
  .partial()
  .refine((v) => Object.keys(v).length > 0, { message: 'Send at least one field to change' });

export const chargeParamsSchema = z.object({ outletId: uuidSchema, chargeId: uuidSchema });

// ---------------------------------------------------------------------------
// Payment methods and order sources

export const PAYMENT_METHOD_KINDS = [
  'cash',
  'card',
  'upi',
  'razorpay',
  'khata',
  'rewards',
  'other',
] as const;
export type PaymentMethodKind = (typeof PAYMENT_METHOD_KINDS)[number];

export const paymentMethodSchema = z.object({
  id: uuidSchema,
  outletId: uuidSchema,
  kind: z.enum(PAYMENT_METHOD_KINDS),
  label: z.string(),
  enabled: z.boolean(),
  sort: z.int(),
  ...archivable,
});
export type PaymentMethodView = z.output<typeof paymentMethodSchema>;

const paymentMethodFields = {
  kind: z.enum(PAYMENT_METHOD_KINDS),
  label: masterNameSchema,
  enabled: z.boolean(),
  sort: sortSchema,
};
export const createPaymentMethodSchema = z.object({
  id: uuidSchema.optional(),
  kind: paymentMethodFields.kind,
  label: paymentMethodFields.label,
  enabled: z.boolean().optional(),
  sort: sortSchema.optional(),
});
export const updatePaymentMethodSchema = z
  .object(paymentMethodFields)
  .partial()
  .refine((v) => Object.keys(v).length > 0, { message: 'Send at least one field to change' });

export const paymentMethodParamsSchema = z.object({
  outletId: uuidSchema,
  paymentMethodId: uuidSchema,
});

export const orderSourceSchema = z.object({
  id: uuidSchema,
  outletId: uuidSchema,
  name: z.string(),
  /** Printed on takeaway/delivery KOTs and bills (e.g. "Pack separately"). */
  packagingNote: z.string().nullable(),
  /** The section (and so the pricing context) its orders use by default. */
  defaultSectionId: uuidSchema.nullable(),
  sort: z.int(),
  ...archivable,
});
export type OrderSourceView = z.output<typeof orderSourceSchema>;

const orderSourceFields = {
  name: masterNameSchema,
  packagingNote: z.string().trim().min(1).max(200).nullable(),
  defaultSectionId: uuidSchema.nullable(),
  sort: sortSchema,
};
export const createOrderSourceSchema = z.object({
  id: uuidSchema.optional(),
  name: orderSourceFields.name,
  packagingNote: z.string().trim().min(1).max(200).optional(),
  defaultSectionId: uuidSchema.optional(),
  sort: sortSchema.optional(),
});
export const updateOrderSourceSchema = z
  .object(orderSourceFields)
  .partial()
  .refine((v) => Object.keys(v).length > 0, { message: 'Send at least one field to change' });

export const orderSourceParamsSchema = z.object({
  outletId: uuidSchema,
  orderSourceId: uuidSchema,
});

// ---------------------------------------------------------------------------
// Feature switches (04-data-model.md `feature_switch`)

/**
 * The features an outlet can switch on or off, with their defaults.
 * 11-open-questions.md Q10: until v3 billing exists, paid add-ons are read from
 * the legacy `entity_marketplace` rows into these switches by CDC.
 * TODO(legacy): map each `entity_marketplace` product to a key here.
 */
export const FEATURE_SWITCHES = {
  qr_ordering: { label: 'QR ordering at tables', default: true },
  online_ordering: { label: 'Takeaway and delivery from the customer app', default: false },
  aggregators: { label: 'Swiggy and Zomato (WeraFood)', default: false },
  own_delivery: { label: 'Delivery partners (Pidge, Borzo)', default: false },
  kds: { label: 'Kitchen display screens', default: false },
  inventory: { label: 'Inventory and recipes', default: false },
  bar: { label: 'Liquor and bar', default: false },
  khata: { label: 'Customer credit (khata)', default: false },
  rewards: { label: 'Rewards', default: false },
} as const satisfies Record<string, { label: string; default: boolean }>;

export type FeatureKey = keyof typeof FEATURE_SWITCHES;
export const FEATURE_KEYS = Object.keys(FEATURE_SWITCHES) as FeatureKey[];
export const featureKeySchema = z.enum(FEATURE_KEYS as [FeatureKey, ...FeatureKey[]]);

export const featureSwitchSchema = z.object({
  key: featureKeySchema,
  label: z.string(),
  enabled: z.boolean(),
  /** True when nobody set it: `enabled` is the default. */
  isDefault: z.boolean(),
  updatedAt: isoTimestampSchema.nullable(),
});
export const featureSwitchListSchema = z.object({ data: z.array(featureSwitchSchema) });
export const setFeatureSwitchSchema = z.object({ enabled: z.boolean() });
export const featureSwitchParamsSchema = z.object({ outletId: uuidSchema, key: featureKeySchema });

// ---------------------------------------------------------------------------
// Terminals

/** 03-modules.md §1: a terminal's role picks the app layout and home screen. */
export const TERMINAL_ROLES = ['pos', 'captain', 'kds', 'hub', 'kiosk', 'backoffice'] as const;
export type TerminalRole = (typeof TERMINAL_ROLES)[number];

export const TERMINAL_PLATFORMS = ['web', 'windows', 'android'] as const;

export const terminalSchema = z.object({
  id: uuidSchema,
  outletId: uuidSchema,
  name: z.string(),
  role: z.enum(TERMINAL_ROLES),
  platform: z.enum(TERMINAL_PLATFORMS),
  appVersion: z.string().nullable(),
  isHub: z.boolean(),
  hubPriority: z.int().nullable(),
  lastSeenAt: isoTimestampSchema.nullable(),
  blockedAt: isoTimestampSchema.nullable(),
  blockedReason: z.string().nullable(),
  createdAt: isoTimestampSchema,
  version: z.int(),
});

export const registerTerminalSchema = z.object({
  name: z.string().trim().min(1).max(60),
  role: z.enum(TERMINAL_ROLES),
  platform: z.enum(TERMINAL_PLATFORMS),
  appVersion: z.string().trim().max(40).optional(),
  isHub: z.boolean().optional(),
  hubPriority: z.int().min(0).max(100).optional(),
  /**
   * 32+ random bytes (base64url) the device generated and keeps in its secure
   * storage. Only a hash is stored; the device proves it is this terminal by
   * sending it with sign-in and PIN requests.
   */
  deviceSecret: deviceSecretSchema,
});

/** `PATCH {o}/terminals/{id}`: rename, change role, hub settings, reported app version. */
export const updateTerminalSchema = z
  .object({
    name: z.string().trim().min(1).max(60),
    role: z.enum(TERMINAL_ROLES),
    appVersion: z.string().trim().max(40).nullable(),
    isHub: z.boolean(),
    hubPriority: z.int().min(0).max(100).nullable(),
  })
  .partial()
  .refine((v) => Object.keys(v).length > 0, { message: 'Send at least one field to change' });

export const blockTerminalSchema = z.object({ reason: z.string().trim().min(1).max(200) });
export const unblockTerminalSchema = z.object({ reason: z.string().trim().min(1).max(200) });

export const terminalParamsSchema = z.object({ outletId: uuidSchema, terminalId: uuidSchema });

/**
 * `POST {o}/terminals/heartbeat`: a till says it is alive (every 30 s or so while it can
 * reach the server). Stamps the session's terminal `last_seen_at`; the aggregators
 * heartbeat rule reads it (06-flows.md F5). `recorded` is false when the stamp was fresh
 * enough already or the session is bound to no terminal of this outlet.
 */
export const heartbeatResultSchema = z.object({
  at: isoTimestampSchema,
  recorded: z.boolean(),
});

// ---------------------------------------------------------------------------
// Bootstrap: one bundle a till caches (05-api.md `GET {o}/bootstrap`, F2 step 1)

export const outletBootstrapSchema = z.object({
  outlet: outletSchema,
  settings: outletSettingsSchema,
  sections: z.array(sectionSchema),
  tables: z.array(diningTableSchema),
  timingGroups: z.array(timingGroupSchema),
  charges: z.array(otherChargeSchema),
  paymentMethods: z.array(paymentMethodSchema),
  orderSources: z.array(orderSourceSchema),
  featureSwitches: z.record(featureKeySchema, z.boolean()),
  /**
   * pricing's master data for offline previews (08-clients.md: the till caches
   * tax rules): the outlet's live tax groups with their rules, its live
   * discounts, and the company's configured discount limits per role.
   */
  taxGroups: z.array(taxGroupSchema),
  discounts: z.array(discountSchema),
  discountLimits: z.array(roleDiscountLimitSchema),
  /**
   * The outlet's published menu version (null before the first publish). The menu
   * itself is `GET {o}/menu?context=` (its own strong ETag); a `menu.published`
   * message on `outlet:{id}:menu` says when to refetch it.
   */
  menuVersion: z.int().nullable(),
  /**
   * The kitchen's live stations (with their printers) and printers (03-modules.md
   * §5): where KOTs go, and which printers this terminal owns (it prints their jobs).
   */
  stations: z.array(stationSchema),
  printers: z.array(printerSchema),
  /**
   * The registered terminal this session is bound to, when it belongs to this
   * outlet. Without `lastSeenAt`: heartbeats must not change the bundle's ETag.
   */
  terminal: terminalSchema.omit({ lastSeenAt: true }).nullable(),
  /** What the caller may do at this outlet (hiding only; the server checks every action). */
  permissions: z.array(permissionKeySchema),
});
export type OutletBootstrap = z.output<typeof outletBootstrapSchema>;

// ---------------------------------------------------------------------------
// Events

const MASTER_CHANGES = ['created', 'updated', 'archived'] as const;
const masterChangeSchema = z.enum(MASTER_CHANGES);

export const companyCreatedV1 = defineEvent(
  'company.created',
  1,
  z.object({ companyId: uuidSchema, name: z.string() }),
);

export const outletCreatedV1 = defineEvent(
  'outlet.created',
  1,
  z.object({ outletId: uuidSchema, name: z.string(), code: z.string() }),
);

/** Profile or settings changed; `fields` names what (`gstin`, `settings.kot.mode`, …). */
export const outletUpdatedV1 = defineEvent(
  'outlet.updated',
  1,
  z.object({ outletId: uuidSchema, fields: z.array(z.string()), version: z.int() }),
);

export const sectionChangedV1 = defineEvent(
  'section.changed',
  1,
  z.object({ sectionId: uuidSchema, change: masterChangeSchema, version: z.int() }),
);

export const tableUpdatedV1 = defineEvent(
  'table.updated',
  1,
  z.object({
    tableId: uuidSchema,
    sectionId: uuidSchema,
    change: z.enum(['created', 'updated', 'archived', 'qr_regenerated']),
    qrVersion: z.int(),
    version: z.int(),
  }),
);

export const timingGroupChangedV1 = defineEvent(
  'timing_group.changed',
  1,
  z.object({ timingGroupId: uuidSchema, change: masterChangeSchema, version: z.int() }),
);

export const otherChargeChangedV1 = defineEvent(
  'other_charge.changed',
  1,
  z.object({ chargeId: uuidSchema, change: masterChangeSchema, version: z.int() }),
);

export const paymentMethodChangedV1 = defineEvent(
  'payment_method.changed',
  1,
  z.object({
    paymentMethodId: uuidSchema,
    kind: z.enum(PAYMENT_METHOD_KINDS),
    change: masterChangeSchema,
    version: z.int(),
  }),
);

export const orderSourceChangedV1 = defineEvent(
  'order_source.changed',
  1,
  z.object({ orderSourceId: uuidSchema, change: masterChangeSchema, version: z.int() }),
);

export const featureSwitchChangedV1 = defineEvent(
  'feature_switch.changed',
  1,
  z.object({ key: featureKeySchema, enabled: z.boolean() }),
);

export const terminalRegisteredV1 = defineEvent(
  'terminal.registered',
  1,
  z.object({
    terminalId: uuidSchema,
    name: z.string(),
    role: z.enum(TERMINAL_ROLES),
    platform: z.enum(TERMINAL_PLATFORMS),
    isHub: z.boolean(),
  }),
);

export const terminalBlockedV1 = defineEvent(
  'terminal.blocked',
  1,
  z.object({ terminalId: uuidSchema, reason: z.string() }),
);

export const terminalUnblockedV1 = defineEvent(
  'terminal.unblocked',
  1,
  z.object({ terminalId: uuidSchema, reason: z.string() }),
);

export const terminalUpdatedV1 = defineEvent(
  'terminal.updated',
  1,
  z.object({ terminalId: uuidSchema, fields: z.array(z.string()), version: z.int() }),
);

/**
 * Org events a till's cached bootstrap depends on (realtime `setup.changed`). The
 * bundle carries the till's own terminal, so its update and unblock count too (a
 * blocked till is signed out instead; a newly registered one fetches its own).
 */
export const ORG_SETUP_EVENTS = [
  outletUpdatedV1,
  sectionChangedV1,
  tableUpdatedV1,
  timingGroupChangedV1,
  otherChargeChangedV1,
  paymentMethodChangedV1,
  orderSourceChangedV1,
  featureSwitchChangedV1,
  terminalUpdatedV1,
  terminalUnblockedV1,
] as const;

export const ORG_EVENTS = [
  companyCreatedV1,
  outletCreatedV1,
  ...ORG_SETUP_EVENTS,
  terminalRegisteredV1,
  terminalBlockedV1,
] as const;
